import { Button } from "@dashboard/components/Button";
import PageHeader from "@dashboard/components/PageHeader";
import { Box, Text } from "@saleor/macaw-ui-next";
import React, { useState } from "react";
import { FormattedMessage } from "react-intl";

import InquiryFormCreateModal from "../components/InquiryFormCreateModal";
import InquiryFormDeleteDialog from "../components/InquiryFormDeleteDialog";
import InquiryFormEditModal from "../components/InquiryFormEditModal";
import { useInquiryFormPageTypesQuery,useInquiryFormsListQuery } from "../hooks";

interface InquiryFormsListProps {
  params?: any;
}

const InquiryFormsList: React.FC<InquiryFormsListProps> = () => {
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [editingForm, setEditingForm] = useState<any>(null);
  const [deletingForm, setDeletingForm] = useState<any>(null);

  // Get the inquiry-forms page type ID
  const { data: pageTypesData } = useInquiryFormPageTypesQuery({
    variables: {
      filter: { slugs: ["inquiry-forms"] },
      first: 1
    }
  });

  const pageTypeId = pageTypesData?.pageTypes?.edges[0]?.node?.id;

  // Get inquiry forms (pages with inquiry-forms page type)
  const { data, loading, refetch } = useInquiryFormsListQuery({
    variables: {
      filter: pageTypeId ? { pageTypes: [pageTypeId] } : { pageTypes: [] },
      first: 100
    },
    skip: !pageTypeId
  });

  const inquiryForms = data?.pages?.edges?.map((edge: any) => edge.node) || [];

  const handleCreateSuccess = () => {
    setIsCreateModalOpen(false);
    refetch();
  };

  const handleEditSuccess = () => {
    setEditingForm(null);
    refetch();
  };

  const handleDeleteSuccess = () => {
    setDeletingForm(null);
    refetch();
  };

  const handleEdit = (form: any) => {
    setEditingForm(form);
  };

  const handleDelete = (form: any) => {
    setDeletingForm(form);
  };

  return (
    <>
      <Box paddingX={6} paddingTop={6}>
        <PageHeader title="Inquiry Forms">
          <Button
            variant="primary"
            onClick={() => setIsCreateModalOpen(true)}
            disabled={!pageTypeId}
            data-test-id="create-inquiry-form-button"
          >
            <FormattedMessage
              id="IHi/T5"
              defaultMessage="Create Inquiry Form"
            />
          </Button>
        </PageHeader>
      </Box>

      <Box paddingX={6} paddingBottom={6}>
        {loading ? (
          <Box display="flex" justifyContent="center" padding={8}>
            <Text>Loading...</Text>
          </Box>
        ) : inquiryForms.length === 0 ? (
          <Box 
            display="flex" 
            flexDirection="column" 
            alignItems="center" 
            justifyContent="center" 
            padding={12}
            style={{ minHeight: "400px" }}
          >
            <Text size={4} color="default2" textAlign="center">
              <FormattedMessage
                id="ovM8UF"
                defaultMessage="No inquiry forms found. Create your first inquiry form to get started."
              />
            </Text>
            <Box marginTop={4}>
              <Button
                variant="primary"
                onClick={() => setIsCreateModalOpen(true)}
                disabled={!pageTypeId}
              >
                <FormattedMessage
                  id="IHi/T5"
                  defaultMessage="Create Inquiry Form"
                />
              </Button>
            </Box>
          </Box>
        ) : (
          <Box display="flex" flexDirection="column" gap={4}>
            {inquiryForms.map((form: any) => (
              <Box
                key={form.id}
                borderColor="default1"
                borderWidth={1}
                borderStyle="solid"
                borderRadius={3}
                padding={6}
                backgroundColor="default1"
                style={{
                  transition: "all 0.2s ease",
                  cursor: "pointer"
                }}
              >
                <Box display="flex" justifyContent="space-between" alignItems="start">
                  <Box display="flex" flexDirection="column" gap={2}>
                    <Text size={4} fontWeight="bold" color="default1">
                      {form.title}
                    </Text>
                    <Text size={3} color="default2" style={{ fontFamily: "monospace" }}>
                      /{form.slug}
                    </Text>
                    <Box display="flex" alignItems="center" gap={2}>
                      <Box
                        paddingX={2}
                        paddingY={1}
                        backgroundColor="default2"
                        borderRadius={2}
                      >
                        <Text size={1} color={form.isPublished ? "success1" : "critical1"} fontWeight="medium">
                          {form.isPublished ? 'Published' : 'Hidden'}
                        </Text>
                      </Box>
                      <Text size={2} color="default2">
                        •
                      </Text>
                      <Text size={2} color="default2">
                        Created {new Date(form.created).toLocaleDateString()}
                      </Text>
                    </Box>
                    {form.metadata && form.metadata.length > 0 && (
                      <Box display="flex" flexWrap="wrap" gap={1} marginTop={1}>
                        {form.metadata.find((m: any) => m.key === 'contact_form' && m.value === 'true') && (
                          <Box
                            paddingX={2}
                            paddingY={1}
                            backgroundColor="default1"
                            borderRadius={2}
                            style={{
                              border: '1px solid',
                              borderColor: 'rgba(56, 118, 255, 0.2)'
                            }}
                          >
                            <Text size={1} color="accent1" fontWeight="medium">Contact Form</Text>
                          </Box>
                        )}
                        {form.metadata.find((m: any) => m.key === 'product_inquiry_form' && m.value === 'true') && (
                          <Box
                            paddingX={2}
                            paddingY={1}
                            backgroundColor="default1"
                            borderRadius={2}
                            style={{
                              border: '1px solid',
                              borderColor: 'rgba(255, 152, 0, 0.2)'
                            }}
                          >
                            <Text size={1} color="warning1" fontWeight="medium">Product Inquiry</Text>
                          </Box>
                        )}
                        {form.metadata.find((m: any) => m.key === 'reCAPTCHA' && m.value === 'true') && (
                          <Box
                            paddingX={2}
                            paddingY={1}
                            backgroundColor="default1"
                            borderRadius={2}
                            style={{
                              border: '1px solid',
                              borderColor: 'rgba(76, 175, 80, 0.2)'
                            }}
                          >
                            <Text size={1} color="success1" fontWeight="medium">reCAPTCHA</Text>
                          </Box>
                        )}
                      </Box>
                    )}
                  </Box>
                  <Box display="flex" gap={2}>
                    <Button 
                      variant="secondary" 
                      size="small"
                      onClick={() => handleEdit(form)}
                    >
                      Edit
                    </Button>
                    <Button 
                      variant="secondary" 
                      size="small"
                      onClick={() => handleDelete(form)}
                    >
                      Delete
                    </Button>
                  </Box>
                </Box>
              </Box>
            ))}
          </Box>
        )}
      </Box>

      <InquiryFormCreateModal
        open={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onSuccess={handleCreateSuccess}
        pageTypeId={pageTypeId}
      />

      <InquiryFormEditModal
        open={!!editingForm}
        onClose={() => setEditingForm(null)}
        onSuccess={handleEditSuccess}
        form={editingForm}
      />

      <InquiryFormDeleteDialog
        open={!!deletingForm}
        onClose={() => setDeletingForm(null)}
        onSuccess={handleDeleteSuccess}
        form={deletingForm}
      />
    </>
  );
};

export default InquiryFormsList;